import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { DataContext } from "./index";
import { WEBSERVICE } from "../../CoreDataService";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useTradingColors } from "./tradingColorConfig";
const TABS = [
  { id: "all", label: "All" },
  { id: "change", label: "Change" },
  { id: "high_low", label: "New High/Low" },
  { id: "fluctuation", label: "Fluctuation" },
  { id: "volume", label: "Volume" },
];

// Mini SVG Sparkline component to display visual trend line
const MiniSparkline = ({ isPositive, strokeColor }) => {
  const stroke =
    strokeColor ||
    (isPositive
      ? "var(--trading-up-color, #0ecb81)"
      : "var(--trading-down-color, #f6465d)");
  const points = isPositive
    ? "2,12 6,9 11,10 16,6 21,7 26,3 30,5 34,1"
    : "2,1 6,5 11,3 16,8 21,7 26,11 30,9 34,13";

  return (
    <svg width="36" height="15" viewBox="0 0 36 15" style={{ overflow: "visible" }}>
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

const TopMovers = ({ isExpanded = false, onToggleExpand, standalone = false }) => {
  const { setCurrencycode, setBasecurrency, currencycode, basecurrency } =
    useContext(DataContext) || {};
  const { upColor, downColor } = useTradingColors();

  const theme = localStorage.getItem("theme");
  const [activeTab, setActiveTab] = useState("all");
  const [assetsList, setAssetsList] = useState([]);
  const [headersList, setHeadersList] = useState(() => {
    try {
      const cached = localStorage.getItem("spot_headers");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [newFiatArray, setNewFiatArray] = useState(() => {
    try {
      const cached = localStorage.getItem("user_fiat_array");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);

  // Fetch user balance to obtain available fiat currencies for this user
  const getUserBalance = () => {
    const customerId = localStorage.getItem("userId");
    const uuid = localStorage.getItem("uuid");
    const accessToken = localStorage.getItem("access_token");

    if (!customerId || !accessToken) return;

    const userTransObj = {
      customerId,
      uuid,
    };

    axios
      .post(`${WEBSERVICE}transaction/getUserBalance`, JSON.stringify(userTransObj), {
        headers: {
          "Content-Type": "application/json",
          authorization: "BEARER " + accessToken,
        },
      })
      .then((response) => {
        const result = response.data;
        const userBalanceList = result?.userBalanceList;

        if (userBalanceList != null && Array.isArray(userBalanceList)) {
          const fiatCurrencies = userBalanceList
            .filter((item) => item.currencyType === 1)
            .map((item) => item.currencyCode);

          setNewFiatArray(fiatCurrencies);
          localStorage.setItem("user_fiat_array", JSON.stringify(fiatCurrencies));
        }
      })
      .catch((err) => {
        console.warn("TopMovers getUserBalance error:", err?.message);
      });
  };

  // Fetch or load all assets data
  const fetchAssets = async () => {
    try {
      const brokerId = localStorage.getItem("BROKERID") || "1";
      const response = await axios.get(
        `https://accounts.paybito.com/CacheService/api/getAssetsData?Name=Assets&BrokerId=${brokerId}`
      );

      if (response.data && response.data.value) {
        const parsed = JSON.parse(response.data.value);
        if (parsed.Values && Array.isArray(parsed.Values)) {
          setAssetsList(parsed.Values);
          if (parsed.Header && Array.isArray(parsed.Header)) {
            setHeadersList(parsed.Header);
            localStorage.setItem("spot_headers", JSON.stringify(parsed.Header));
          }
          localStorage.setItem("spot_assets", JSON.stringify(parsed.Values));
          return;
        }
      }
    } catch (err) {
      // Fallback to localStorage cache
      const cached = localStorage.getItem("spot_assets");
      if (cached) {
        try {
          const parsedCached = JSON.parse(cached);
          if (Array.isArray(parsedCached)) {
            setAssetsList(parsedCached);
          }
        } catch (e) {
          console.error("Error parsing cached spot_assets:", e);
        }
      }
      const cachedHeaders = localStorage.getItem("spot_headers");
      if (cachedHeaders) {
        try {
          const parsedHeaders = JSON.parse(cachedHeaders);
          if (Array.isArray(parsedHeaders)) {
            setHeadersList(parsedHeaders);
          }
        } catch (e) {}
      }
    }
  };

  useEffect(() => {
    // Initial load from cache if available for instant display
    const cached = localStorage.getItem("spot_assets");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          setAssetsList(parsed);
        }
      } catch (e) {}
    }

    const cachedHeaders = localStorage.getItem("spot_headers");
    if (cachedHeaders) {
      try {
        const parsed = JSON.parse(cachedHeaders);
        if (Array.isArray(parsed) && parsed.length) {
          setHeadersList(parsed);
        }
      } catch (e) {}
    }

    const cachedFiats = localStorage.getItem("user_fiat_array");
    if (cachedFiats) {
      try {
        const parsed = JSON.parse(cachedFiats);
        if (Array.isArray(parsed) && parsed.length) {
          setNewFiatArray(parsed);
        }
      } catch (e) {}
    }

    const handleFiatUpdate = (e) => {
      if (e.detail && Array.isArray(e.detail)) {
        setNewFiatArray(e.detail);
      } else {
        try {
          const c = localStorage.getItem("user_fiat_array");
          if (c) setNewFiatArray(JSON.parse(c));
        } catch (err) {}
      }
    };
    window.addEventListener("user_fiat_array_updated", handleFiatUpdate);

    getUserBalance();
    fetchAssets();
    const interval = setInterval(fetchAssets, 15000); // Poll every 15s
    return () => {
      window.removeEventListener("user_fiat_array_updated", handleFiatUpdate);
      clearInterval(interval);
    };
  }, []);

  // Format time (e.g. 12:22:07)
  const getCurrentFormattedTime = (offsetSeconds = 0) => {
    const d = new Date(Date.now() - offsetSeconds * 1000);
    return d.toTimeString().split(" ")[0];
  };

  // Process & categorize movers based on the active tab
  const processedMovers = useMemo(() => {
    if (!assetsList || !assetsList.length) return [];

    // Filter only those assets that are available for that user (matching LeftTable orderbook logic)
    const validAssets = assetsList.filter((a) => {
      if (!a.currencyCode || !a.baseCurrency) return false;

      const baseUpper = a.baseCurrency?.toUpperCase();
      const isFiat = Number(a.currencyType) === 1;

      // Fiat pairs (currencyType === 1): restricted to user's available fiat currencies (newFiatArray)
      if (isFiat) {
        if (newFiatArray && newFiatArray.length > 0) {
          return newFiatArray.some((f) => f?.toUpperCase() === baseUpper);
        }
        // If user has not loaded fiat balance or fiat array is empty, restrict fiat pairs
        return false;
      }

      // Crypto pairs (currencyType !== 1):
      // Must match one of the available crypto base headers (e.g. BTC, ETH, USDT) if headers exist
      if (headersList && headersList.length > 0) {
        return headersList.some((h) => h?.toUpperCase() === baseUpper);
      }

      return true;
    });

    // Map raw assets to structured mover objects
    const mapped = validAssets.map((a, idx) => {
      let rawRoc = parseFloat(a.roc || 0);
      // Clean corrupt demo outlier data (e.g. -1631% with 0 volume)
      if (rawRoc < -100) rawRoc = -99.9;
      if (rawRoc > 1000) rawRoc = 999;

      const roc = rawRoc;
      const volume = parseFloat(a.volume || 0);
      const ltp = parseFloat(a.ltpValue || 0);
      const high = parseFloat(a.highPrice || 0);
      const low = parseFloat(a.lowPrice || 0);
      const isPositive = roc >= 0;

      // Calculate swing / volatility
      let swing = Math.abs(roc);
      if (high > 0 && low > 0 && high >= low) {
        swing = ((high - low) / low) * 100;
      }

      // Default tag based on conditions
      let tag = "24h Mover";
      if (roc >= 7) {
        tag = "New 24hr High";
      } else if (roc <= -7) {
        tag = "New 24hr Low";
      } else if (roc > 0) {
        tag = "Rapid Rise";
      } else if (roc < 0) {
        tag = "Pullback";
      }

      if (volume > 20000 && Math.abs(roc) < 5) {
        tag = "Volume Surge";
      }

      const timeStr = getCurrentFormattedTime((idx % 12) * 18);

      return {
        ...a,
        currencycode: a.currencyCode,
        basecurrency: a.baseCurrency,
        pair: `${a.currencyCode}/${a.baseCurrency}`,
        roc,
        volume,
        ltp,
        high,
        low,
        swing,
        isPositive,
        tag,
        timeStr,
      };
    });

    let result = [];

    switch (activeTab) {
      case "change": {
        // TAB: CHANGE (Top Gainers first, followed by top movers)
        const gainers = mapped
          .filter((a) => a.roc > 0)
          .sort((a, b) => b.roc - a.roc)
          .map((a, i) => ({
            ...a,
            tag: a.roc >= 10 ? "Top Gainer" : "Rapid Rise",
            timeStr: getCurrentFormattedTime(i * 12 + 4),
          }));

        const losers = mapped
          .filter((a) => a.roc < 0)
          .sort((a, b) => a.roc - b.roc)
          .map((a, i) => ({
            ...a,
            tag: a.roc <= -10 ? "Top Loser" : "Pullback",
            timeStr: getCurrentFormattedTime((i + gainers.length) * 12 + 6),
          }));

        result = [...gainers, ...losers];
        break;
      }

      case "high_low": {
        // TAB: NEW HIGH / LOW (Breakouts to 24h Highs & breakdowns to 24h Lows)
        const highs = mapped
          .filter((a) => a.roc >= 3)
          .sort((a, b) => b.roc - a.roc)
          .map((a, i) => ({
            ...a,
            tag: "New 24hr High",
            timeStr: getCurrentFormattedTime(i * 15 + 2),
          }));

        const lows = mapped
          .filter((a) => a.roc <= -3)
          .sort((a, b) => a.roc - b.roc)
          .map((a, i) => ({
            ...a,
            tag: "New 24hr Low",
            timeStr: getCurrentFormattedTime(i * 15 + 7),
          }));

        result = [...highs, ...lows];
        if (result.length === 0) {
          result = [...mapped].sort((a, b) => Math.abs(b.roc) - Math.abs(a.roc));
        }
        break;
      }

      case "fluctuation": {
        // TAB: FLUCTUATION (Highest price swing & oscillation volatility)
        result = mapped
          .filter((a) => Math.abs(a.roc) >= 0.5 && Math.abs(a.roc) <= 75)
          .sort((a, b) => b.swing - a.swing)
          .map((a, i) => {
            let fTag = "Wide Swing";
            if (a.roc > 4) fTag = "Rapid Rise";
            else if (a.roc < -4) fTag = "Pullback";
            else fTag = "High Volatility";

            return {
              ...a,
              tag: fTag,
              timeStr: getCurrentFormattedTime(i * 14 + 5),
            };
          });
        break;
      }

      case "volume": {
        // TAB: VOLUME (Highest 24h Trading Volume)
        result = mapped
          .filter((a) => a.volume > 0)
          .sort((a, b) => b.volume - a.volume)
          .map((a, i) => ({
            ...a,
            tag: i < 3 ? "Volume Surge" : "High 24h Vol",
            timeStr: getCurrentFormattedTime(i * 16 + 3),
          }));
        break;
      }

      case "all":
      default: {
        // TAB: ALL (Curated multi-category market activity feed)
        const topHigh = mapped
          .filter((a) => a.roc >= 5)
          .sort((a, b) => b.roc - a.roc)
          .slice(0, 3)
          .map((a, i) => ({
            ...a,
            tag: "New 24hr High",
            timeStr: getCurrentFormattedTime(i * 20 + 2),
          }));

        const topVol = mapped
          .filter((a) => a.volume > 0)
          .sort((a, b) => b.volume - a.volume)
          .slice(0, 3)
          .map((a, i) => ({
            ...a,
            tag: "Volume Surge",
            timeStr: getCurrentFormattedTime(i * 20 + 5),
          }));

        const topGainers = mapped
          .filter((a) => a.roc > 0)
          .sort((a, b) => b.roc - a.roc)
          .slice(0, 3)
          .map((a, i) => ({
            ...a,
            tag: "Rapid Rise",
            timeStr: getCurrentFormattedTime(i * 20 + 8),
          }));

        const topSwings = mapped
          .filter((a) => a.roc < -3)
          .sort((a, b) => a.roc - b.roc)
          .slice(0, 3)
          .map((a, i) => ({
            ...a,
            tag: "Pullback",
            timeStr: getCurrentFormattedTime(i * 20 + 11),
          }));

        const seen = new Set();
        const combined = [];
        [...topHigh, ...topVol, ...topGainers, ...topSwings, ...mapped].forEach((item) => {
          if (!seen.has(item.pair)) {
            seen.add(item.pair);
            combined.push(item);
          }
        });

        result = combined;
        break;
      }
    }

    return result.slice(0, 30);
  }, [assetsList, activeTab, newFiatArray, headersList]);

  // Handle clicking a mover row to switch current pair
  const handleSelectPair = (item) => {
    if (!item) return;
    const cCode = item.currencycode?.toUpperCase();
    const bCode = item.basecurrency?.toUpperCase();

    if (setCurrencycode && setBasecurrency) {
      setCurrencycode(cCode);
      setBasecurrency(bCode);
    }

    localStorage.setItem("buying_crypto_asset", cCode.toLowerCase());
    localStorage.setItem("selling_crypto_asset", bCode.toLowerCase());
    if (item.amountPrecision) {
      localStorage.setItem("amountprc", item.amountPrecision);
    }
    if (item.pricePrecision) {
      localStorage.setItem("priceprc", item.pricePrecision);
    }
    if (item.assetCode) {
      localStorage.setItem("assetCode", item.assetCode);
    }
    localStorage.setItem("isAssetPairChangedRecently", "true");

    let assetChangeCounter = localStorage.getItem("assetChangeCounterForSpot");
    assetChangeCounter = assetChangeCounter ? parseInt(assetChangeCounter) + 1 : 1;
    localStorage.setItem("assetChangeCounterForSpot", assetChangeCounter);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        backgroundColor: standalone ? "transparent" : "var(--body-color-less)",
        color: "var(--text-color)",
        fontSize: "12px",
        overflow: "hidden",
        borderTop: !standalone ? "1px solid rgba(125, 125, 125, 0.15)" : undefined,
      }}
    >
      {/* ── TOP HEADER ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: standalone ? "6px 12px 5px 12px" : "4px 8px 3px 10px",
          borderBottom: "1px solid rgba(125, 125, 125, 0.1)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: standalone ? "12px" : "11px",
            letterSpacing: "0.01em",
            color: theme === "light" ? "#1e2329" : "#eaecef",
          }}
        >
          Top Movers
        </span>

        {/* ── TOGGLE ARROW (Increases height by +20% / decreases back) ── */}
        {onToggleExpand && (
          <button
            onClick={onToggleExpand}
            title={isExpanded ? "Collapse Top Movers (decrease height)" : "Expand Top Movers (increase height)"}
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2px 4px",
              borderRadius: "4px",
              color: "#848e9c",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color =
                theme === "light" ? "#1e2329" : "#ffffff")
            }
            onMouseLeave={(e) => (e.currentTarget.style.color = "#848e9c")}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.25s ease",
              }}
            >
              {/* Chevron Up icon: points up when collapsed (expand upward), points down when expanded */}
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
        )}
      </div>

      {/* ── TAB PILLS ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: standalone ? "5px" : "3px",
          padding: standalone ? "4px 10px 5px 10px" : "2px 6px 3px 6px",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          flexShrink: 0,
          borderBottom: "1px solid rgba(125, 125, 125, 0.08)",
        }}
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: isActive
                  ? theme === "light"
                    ? "#e0e3e8"
                    : "rgba(255, 255, 255, 0.12)"
                  : "transparent",
                color: isActive
                  ? theme === "light"
                    ? "#1e2329"
                    : "#ffffff"
                  : "#848e9c",
                border: "none",
                outline: "none",
                borderRadius: "8px",
                padding: standalone ? "3px 8px" : "1px 6px",
                fontSize: standalone ? "10.5px" : "9.5px",
                fontWeight: isActive ? 600 : 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── LIST OF MOVERS ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          padding: standalone ? "6px 8px" : "1px 0",
          display: standalone ? "grid" : "block",
          gridTemplateColumns: standalone
            ? "repeat(auto-fill, minmax(210px, 1fr))"
            : undefined,
          gap: standalone ? "6px" : undefined,
          alignContent: "start",
        }}
      >
        {processedMovers.length ? (
          processedMovers.map((m, idx) => {
            const isCurrentPair =
              m.currencycode?.toUpperCase() === currencycode?.toUpperCase() &&
              m.basecurrency?.toUpperCase() === basecurrency?.toUpperCase();

            return (
              <div
                key={`${m.pair}-${idx}`}
                onClick={() => handleSelectPair(m)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: standalone ? "5px 8px" : "2px 10px",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease, border-color 0.15s ease",
                  backgroundColor: isCurrentPair
                    ? "rgba(255, 255, 255, 0.06)"
                    : standalone
                      ? "rgba(125, 125, 125, 0.04)"
                      : "transparent",
                  border: standalone
                    ? isCurrentPair
                      ? "1px solid #f0b90b"
                      : "1px solid rgba(125, 125, 125, 0.08)"
                    : undefined,
                  borderLeft: !standalone
                    ? isCurrentPair
                      ? "2px solid #f0b90b"
                      : "2px solid transparent"
                    : undefined,
                  borderRadius: standalone ? "8px" : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!isCurrentPair) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 255, 255, 0.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrentPair) {
                    e.currentTarget.style.backgroundColor = standalone
                      ? "rgba(125, 125, 125, 0.04)"
                      : "transparent";
                  }
                }}
              >
                {/* Left: Pair Symbol & Timestamp */}
                <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "10.5px",
                      color: theme === "light" ? "#1e2329" : "#eaecef",
                    }}
                  >
                    {m.pair}
                  </span>
                  <span
                    style={{
                      fontSize: "8.5px",
                      color: "#848e9c",
                    }}
                  >
                    {m.timeStr}
                  </span>
                </div>

                {/* Center/Right: % Change & Event Tag */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "1px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "10.5px",
                      color: m.isPositive
                        ? upColor || "var(--trading-up-color, #0ecb81)"
                        : downColor || "var(--trading-down-color, #f6465d)",
                    }}
                  >
                    {m.isPositive ? `+${m.roc.toFixed(2)}%` : `${m.roc.toFixed(2)}%`}
                  </span>
                  <span
                    style={{
                      fontSize: "8.5px",
                      color: "#848e9c",
                    }}
                  >
                    {m.tag}
                  </span>
                </div>

                {/* Far Right: Mini Sparkline / Trend icon */}
                <div
                  style={{
                    marginLeft: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MiniSparkline
                    isPositive={m.isPositive}
                    strokeColor={m.isPositive ? upColor : downColor}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              color: "#848e9c",
              fontSize: "11px",
            }}
          >
            No movers data available
          </div>
        )}
      </div>


    </div>
  );
};

export default TopMovers;
